import { Fragment } from "react/jsx-runtime";
import CustomText from "../atomic/CustomText";
import Button from "../organism/Button";
import useProfile from "../../services/utils/custom hooks/useProfile";
import { useContext, useRef, useState } from "react";
import { ApplicationState } from "../../services/context/ApplicationStateContext";
import FileUploader from "../global/FileUploader";
import OnlineOrderValuesModel from "../../services/models/formModels/onlineOrderValues.model";
import { Formik } from "formik";
import FieldRenderer from "../global/forms/FieldRenderer";
import CustomTextInputProps from "../../services/models/customTextInputProps.mode";
import CustomCheckBoxInputProps from "../../services/models/customCheckBoxInput.model";
import CustomRadioInputProps from "../../services/models/customRadioInput.model";
import useEnableLOMN from "../../services/utils/custom hooks/OnlineOrder/useEnableLOMN";
import SecurityInfoFields from "../formsFields/SecurityInfo";
import validations from "../../services/validations";
import useGetPresignedUrl from "../../services/utils/custom hooks/Documents/useGetPresignedUrl";
import useClassifyAnalyzeDocumentPatientInfoCheck from "../../services/utils/custom hooks/Approvals/useClassifyAnalyzeDocumentPatientInfoCheck";
import axios from "axios";
import toastMessages from "../../services/utils/helpers/toastMessage";
import BlankDocumentSubmissionBlockedModal from "../molecular/BlankDocumentSubmissionBlockedModal";
import {
  normalizeUploadFile,
  normalizeUploadFileName,
} from "../../services/utils/helpers/fileNameNormalizer";

function TestSubmissionPopup({
  initialValues,
  mutate,
  isPending,
}: {
  initialValues: OnlineOrderValuesModel;
  mutate: Function;
  isPending: boolean;
}): JSX.Element {
  const { data } = useProfile();
  const profile = data?.user;
  const getPresignedUrl = useGetPresignedUrl();
  const roles = profile?.roles ?? [];
  const { showCheckbox, isFetching } = useEnableLOMN();
  const isPhysicianOnly = roles.includes("Physician") && roles.length === 1;
  const isOfficeManagerOnly =
    roles.includes("Office Manager") && roles.length === 1;
  const isCOA = profile?.roles?.includes("Collaborative Order Agreement");
  const {
    state: { selectedPhysician },
  } = useContext(ApplicationState);
  
  const ssnMrnMissing =
    (initialValues?.testTypeId === 4 || initialValues?.testTypeId === 7) &&
    !initialValues?.patientInfo?.securityInfo?.ssn &&
    !initialValues?.patientInfo?.securityInfo?.mrn;

  const [step, setStep] = useState(ssnMrnMissing ? 1 : 2);
  const [uploading, setUploading] = useState(false);
  const [sofPatientCheckInProgress, setSofPatientCheckInProgress] = useState(false);
  const [blockedModalVisible, setBlockedModalVisible] = useState(false);
  const [blockedDisplayName, setBlockedDisplayName] = useState<string | undefined>(
    undefined
  );
  const patientInfoCheck = useClassifyAnalyzeDocumentPatientInfoCheck();
  const sofVerifiedCacheRef = useRef<{
    path: string;
    name: string;
    size: number;
    lastModified: number;
  } | null>(null);
  const setFieldValueRef = useRef<
    ((field: string, value: unknown, shouldValidate?: boolean) => void) | null
  >(null);

  const isDualRolePhysician = roles.length === 2 && roles.includes("Physician");

  const sameAsSelectedPhysician =
    Number(initialValues.physicianId) === profile?.id;
  const directSubmission =
    (roles.includes("Physician") && roles.length === 1) ||
    (isDualRolePhysician && sameAsSelectedPhysician);

  const uploadFileToPresignedUrl = async (file: File) => {
    try {
      const normalizedFile = normalizeUploadFile(file);
      const fileName = normalizeUploadFileName(normalizedFile.name);
      const timestamp = Date.now();
      const lastDotIndex = fileName.lastIndexOf(".");
      const extension = lastDotIndex > 0 ? fileName.slice(lastDotIndex) : "";
      const baseName = lastDotIndex > 0 ? fileName.slice(0, lastDotIndex) : fileName;
      const uniqueFileName = `${baseName}_${timestamp}${extension}`;

      const presignedUrlResponse = await getPresignedUrl.mutateAsync(uniqueFileName);
      const { url, filepath } = presignedUrlResponse.data;

      await axios.put(url, normalizedFile, {
        headers: {
          "Content-Type": normalizedFile.type,
          "x-ms-blob-type": "BlockBlob",
        },
      });

      return filepath; // Return the filepath for reference

    } catch (error: any) {
      const { response } = error;
      if (response) {
        if (response?.data.message) {
          toastMessages.error(response.data.message);
        } else {
          toastMessages.error(error.message ?? "Failed to upload file");
        }
      } else {
        toastMessages.error(error.message ?? "Failed to upload file");
      }
      throw error;
    }
  };

  function sofFileMatchesVerifiedCache(file: File): boolean {
    const c = sofVerifiedCacheRef.current;
    return (
      !!c &&
      file.name === c.name &&
      file.size === c.size &&
      file.lastModified === c.lastModified
    );
  }

  const handleFormSubmit = async (values: OnlineOrderValuesModel) => {
    try {
      setUploading(true);

      let updatedValues = { ...values };

      if (values.sofFile && values.sofFile instanceof File) {
        const file = values.sofFile;
        let filePath: string;

        if (sofFileMatchesVerifiedCache(file)) {
          filePath = sofVerifiedCacheRef.current!.path;
        } else {
          filePath = await uploadFileToPresignedUrl(file);
          const checkData = await patientInfoCheck.mutateAsync(filePath);
          if (checkData?.complete === false) {
            setBlockedDisplayName(file.name);
            setBlockedModalVisible(true);
            setFieldValueRef.current?.("sofFile", null);
            sofVerifiedCacheRef.current = null;
            return;
          }
          sofVerifiedCacheRef.current = {
            path: filePath,
            name: file.name,
            size: file.size,
            lastModified: file.lastModified,
          };
        }

        updatedValues = {
          ...values,
          sofFile: filePath,
        };
      }

      const { sofExpiryDate, ...payloadValues } = updatedValues;
      mutate(payloadValues);
    } catch (error: any) {
      toastMessages.error(error.message ?? "Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  function dismissSofBlockedModal() {
    setBlockedModalVisible(false);
    setBlockedDisplayName(undefined);
    sofVerifiedCacheRef.current = null;
    setFieldValueRef.current?.("sofFile", null);
  }

  function FooterButtons({
    isValid,
    values,
    handleSubmit,
  }: {
    isValid: boolean;
    values: OnlineOrderValuesModel;
    handleSubmit: Function;
  }) {
    const isSubmitLoading = isPending || uploading;
    const isSubmitDisabled = isSubmitLoading || sofPatientCheckInProgress;

    if (directSubmission) {
      return (
        <div className="w-full flex justify-end">
          <Button
            textInformation={{
              text: "Submit",
              fontWeight: "font-normal",
            }}
            onClick={() => handleSubmit()}
            buttonTheme="primary"
            className="self-end"
            type="button"
            disabled={isSubmitDisabled || !isValid}
            loading={isSubmitLoading}
          />
        </div>
      );
    }

    return (
      <div className="w-full flex justify-end">
        {step === 1 && ssnMrnMissing ? (
          <Button
            textInformation={{
              text: "Next",
              fontWeight: "font-normal",
            }}
            onClick={() => setStep(2)}
            buttonTheme="primary"
            className="self-end"
            type="button"
            disabled={!isValid}
          />
        ) : (
          <div
            className={`flex ${ssnMrnMissing
              ? "justify-between items-center"
              : "justify-center items-center"
              }  w-full`}
          >
            {ssnMrnMissing && (
              <Button
                textInformation={{
                  text: "back",
                  fontWeight: "font-normal",
                }}
                onClick={() => setStep(1)}
                buttonTheme="primary"
                className="self-end"
                type="button"
              />
            )}

            <Button
              textInformation={{
                text: isCOA
                  ? "Submit"
                  : values.isPhysicianSignPermission || (!sameAsSelectedPhysician && values.sofFile)
                    ? "Submit"
                    : "Send for approval",
                fontWeight: "font-normal",
              }}
              onClick={() => handleSubmit()}
              buttonTheme="primary"
              className="self-end"
              type="button"
              disabled={
                isSubmitDisabled ||
                (isPhysicianOnly && values.isPhysicianSignPermission === false)
              }
              loading={isSubmitLoading}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <Fragment>
    <div className="max-w-[80vw] md:max-w-[750px] flex flex-col relative max-h-[70vh] overflow-auto py-4">
      {isFetching && (
        <div className="w-full absolute h-full justify-center items-center flex flex-col">
          <img
            src={require("../../assets/images/loadingPNG.png")}
            className={"h-[50px] w-[50px] animate-spin"}
            alt="loading indicator"
          />
        </div>
      )}
      {step === 1 ? (
        <CustomText
          fontWeight="font-normal"
          className="text-md mb-4"
          text="For test submission, either the SSN or MRN is mandatory. You may still provide both of the information if you wish."
        />
      ) : (
        !directSubmission && (
          <div className="mb-4">
            <CustomText
              text="Since you are not the ordering clinician, this order requires electronic authorization from the ordering clinician or an uploaded signed order document completed by the ordering clinician."
              fontWeight="font-normal"
              fontSize="text-sm"
            />
            {/* <div className="bg-sideMenuHoverBg p-6 border border-dashed rounded-md border-dropDownBorder">
              <CustomText
                text="New SOF (signature on file) creation has been disabled. Please use Clinician Approval workflow (option B in Ordering Instructions Guide) and reach out if you have any questions."
                fontWeight="font-normal"
                className="text-requiredRed"
              />
            </div> */}
          </div>
        )
      )}
      <Formik
        enableReinitialize
        initialValues={{
          ...initialValues,
          isPhysicianSignPermission: false,
          sofFile: null,
        }}
        validateOnMount
        validationSchema={step === 1 && validations.securityInfoValidation}
        isInitialValid={false}
        onSubmit={handleFormSubmit}
      >
        {({
          values,
          errors,
          touched,
          getFieldMeta,
          setFieldValue,
          handleSubmit,
          setFieldTouched,
          isSubmitting,
          handleBlur,
          handleChange,
          isValid,
          getFieldProps,
        }) => {
          setFieldValueRef.current = setFieldValue;
          const sofExpiryDate = values?.sofExpiryDate
            ? new Date(values.sofExpiryDate)
            : null;
          const isValidSofExpireDate =
            !!sofExpiryDate && !Number.isNaN(sofExpiryDate.getTime());
          const sixtyDaysAfterSofExpiry = isValidSofExpireDate
            ? new Date(sofExpiryDate.getTime() + 60 * 24 * 60 * 60 * 1000)
            : null;
          const isWithinSofExpiryWindow =
            !!sixtyDaysAfterSofExpiry && new Date() <= sixtyDaysAfterSofExpiry;
          const isExpiredSofForOfficeManager =
            isOfficeManagerOnly && values?.isExpiredSOF;
          const isExpiredPastSixtyDays =
            isExpiredSofForOfficeManager &&
            isValidSofExpireDate &&
            !isWithinSofExpiryWindow;
          const shouldBlockWetSignature =
            isExpiredSofForOfficeManager &&
            (!isValidSofExpireDate || isWithinSofExpiryWindow);
          const shouldRenderWetSignatureSection =
            !profile.roles.includes("Collaborative Order Agreement") &&
            !isFetching &&
            (isOfficeManagerOnly ? !isExpiredPastSixtyDays : !showCheckbox);

          const isWetSignatureOnFileAcknowledgment =
            values?.testTypeId !== 8 &&
            !(
              profile?.roles?.includes("Collaborative Order Agreement") &&
              selectedPhysician !== profile?.id
            );

          return (
            <Fragment>
              <div
                className={`mb-4 ${step === 1 && !ssnMrnMissing && "h-64"}`}
              >
                {step === 1 && ssnMrnMissing ? (
                  <div>
                    <SecurityInfoFields
                      touched={touched}
                      errors={errors}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      values={values}
                      setFieldValue={setFieldValue}
                      getFieldMeta={getFieldMeta}
                      submission
                    />
                  </div>
                ) : (
                  shouldRenderWetSignatureSection &&
                  (
                    <div>
                      {shouldBlockWetSignature && (
                        <div className="mb-4 bg-sideMenuHoverBg p-4 border border-dashed rounded-md border-dropDownBorder">
                          <CustomText
                            text="The wet signature on file has expired. Please send order for approval."
                            fontWeight="font-normal"
                            className="text-requiredRed"
                          />
                        </div>
                      )}
                      <div className="bg-sideMenuHoverBg p-4 border border-dashed rounded-md border-dropDownBorder">
                        <FieldRenderer
                          fields={[
                            {
                              labelInfo: {
                                label: "",
                                inputId: "isPhysicianSignPermission",
                                required:
                                  (profile?.roles?.length == 1 &&
                                    profile?.roles?.includes("Physician") &&
                                    values.testTypeId == 8) ||
                                  profile?.roles?.includes(
                                    "Collaborative Order Agreement"
                                  ),
                              },
                              name: "isPhysicianSignPermission",
                              disabled: isFetching,
                              checkBoxes: [
                                {
                                  label:
                                    values?.testTypeId === 8
                                      ? "By my signature below, I, the healthcare provider, authorize performance of NeuroIDgenetix 902 and indicate that the purpose of the test(s), the procedures, the benefits and risks involved have been explained to the patient and patient informed consent has been obtained in accordance with state and local laws. I attest that this patient has a diagnosis for which pharmacologic therapy is reasonable and necessary, and that the drug or drugs that I intend to prescribe are reasonable and necessary for the treatment of this patient's diagnosis. I have made an initial personalized decision for this patient based on the patient's non-genetic factors. I have a record of what drug(s) is/are being considered and for what indication(s) to ensure the test(s) performed is reasonable and necessary. The test results will be utilized by me to determine a dose change to the patient's current medication and/or prescribe a new medication. I have informed the patient that Castle Biosciences, Inc. may be an out-of-network provider under their insurance plan and that I do not have any financial interest in Castle Biosciences, Inc."
                                      : profile?.roles?.includes(
                                        "Collaborative Order Agreement"
                                      ) && selectedPhysician !== profile?.id
                                        ? "By selecting this checkbox, I hereby acknowledge, that I will be submitting this order on behalf of the clinician's collaborative ordering agreement."
                                        : "By selecting this checkbox, I hereby acknowledge, that I will be submitting this order on behalf of the clinician using the wet signature on file.",
                                  id: "physicianSignPermission",
                                  value: true,
                                  disabled:
                                    isFetching ||
                                    values?.sofMismatch ||
                                    shouldBlockWetSignature ||
                                    (!!values?.noSOF &&
                                      isWetSignatureOnFileAcknowledgment),
                                },
                              ],
                            },
                          ].map((field, parentIndex) => {
                            return {
                              ...field,
                              errors,
                              touched,
                              setFieldValue,
                              values,
                              setFieldTouched,
                              parentContainerClass: `${(field as CustomTextInputProps)
                                ?.parentContainerClass
                                }`,
                              getFieldMeta,
                              ...getFieldProps(
                                (
                                  field as
                                  | CustomCheckBoxInputProps
                                  | CustomTextInputProps
                                  | CustomRadioInputProps
                                )?.labelInfo?.inputId ?? ""
                              ),
                            };
                          })}
                        />
                      </div>
                    </div>
                  )
                )}
              </div>
              {!sameAsSelectedPhysician && (
                <div className="bg-sideMenuHoverBg flex flex-col items-center p-6 border border-dashed rounded-md border-dropDownBorder mt-4 mb-4">
                  <CustomText
                    text="Upload a signed order document"
                    fontWeight="font-normal"
                    className="mb-4"
                  />
                  <div className="flex flex-col items-center text-center mb-4">
                    <CustomText
                      text="Accepted documents :"
                      fontWeight="font-semibold"
                      fontSize="text-xs"
                      className="mb-1"
                    />
                    <CustomText
                      text="Order Summary or Test Requisition Form (signed by the ordering clinician)"
                      fontWeight="font-thin"
                      fontSize="text-xs"
                      className="max-w-[280px]"
                    />
                  </div>
                  {sofPatientCheckInProgress && (
                    <div className="mb-3 w-full max-w-[300px] rounded-md border border-primary bg-lightBlue px-3 py-2">
                      <CustomText
                        text="Uploading your file and verifying patient information. This can take up to a minute — please wait before submitting."
                        fontWeight="font-medium"
                        fontSize="text-sm"
                        className="text-royalBlue"
                      />
                    </div>
                  )}
                  <FileUploader
                    name="sof"
                    id="sof"
                    className="md:max-w-[300px] max-w-[200px]"
                    labelInfo={{
                      label: "Accepted formats include .pdf, .png, .jpeg, and .doc/*, with a maximum file size of 50 MB.",
                    }}
                    onChange={(e) => {
                      const { value } = e.target;
                      setFieldValue("sofFile", value);
                      if (!(value instanceof File)) {
                        sofVerifiedCacheRef.current = null;
                        return;
                      }
                      void (async () => {
                        setSofPatientCheckInProgress(true);
                        try {
                          const filepath = await uploadFileToPresignedUrl(value);
                          const checkData = await patientInfoCheck.mutateAsync(filepath);
                          if (checkData?.complete === false) {
                            setBlockedDisplayName(value.name);
                            setBlockedModalVisible(true);
                            setFieldValue("sofFile", null);
                            sofVerifiedCacheRef.current = null;
                            return;
                          }
                          sofVerifiedCacheRef.current = {
                            path: filepath,
                            name: value.name,
                            size: value.size,
                            lastModified: value.lastModified,
                          };
                        } catch (err: any) {
                          const msg =
                            err?.response?.data?.message ??
                            err?.message ??
                            "Upload or patient information check failed.";
                          toastMessages.error(msg);
                          setFieldValue("sofFile", null);
                          sofVerifiedCacheRef.current = null;
                        } finally {
                          setSofPatientCheckInProgress(false);
                        }
                      })();
                    }}
                    setFieldTouched={setFieldTouched}
                    value={values.sofFile}
                    whiteBg
                    approval
                    disabled={sofPatientCheckInProgress || uploading}
                  />
                </div>
              )}
              <div className="mt-4">
                <FooterButtons
                  isValid={isValid}
                  values={values}
                  handleSubmit={handleSubmit}
                />
              </div>
            </Fragment>
          );
        }}
      </Formik>
    </div>
    <BlankDocumentSubmissionBlockedModal
      visible={blockedModalVisible}
      onClose={dismissSofBlockedModal}
      onReplaceFile={dismissSofBlockedModal}
      issueQuotedFileName={blockedDisplayName}
    />
    </Fragment>
  );
}

export default TestSubmissionPopup;
